/**
 * Generacion del PDF de factura con el formato de ARCA (ex AFIP).
 *
 * IMPORTANTE: esto es una demo. No hay conexion con los web services de ARCA:
 * el CAE, su vencimiento y el numero de comprobante se simulan localmente. El
 * objetivo es que el papel salga identico al de una factura electronica real
 * emitida por Ardel (RG 4291: encabezado partido, recuadro de letra, codigo de
 * barras I2of5 y QR del comprobante).
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Order } from '../types';
import { generateQrMatrix } from '../utils/qr';

/* ------------------------------------------------------------- tipos de comprobante */

export type TipoFactura = 'A' | 'B' | 'C';

interface TipoFacturaDef {
    letra: TipoFactura;
    /** Codigo de comprobante de ARCA (01 = Factura A, 06 = B, 11 = C). */
    codigo: string;
    nombre: string;
    /** Descripcion del destinatario tipico, para la UI. */
    destinatario: string;
    /**
     * En A el IVA se discrimina: los precios de la venta se toman como finales
     * y se les extrae el 21%. En B y C el importe va con el IVA incluido.
     */
    discriminaIva: boolean;
}

export const TIPOS_FACTURA: Record<TipoFactura, TipoFacturaDef> = {
    A: {
        letra: 'A',
        codigo: '01',
        nombre: 'Factura A',
        destinatario: 'Responsable Inscripto',
        discriminaIva: true,
    },
    B: {
        letra: 'B',
        codigo: '06',
        nombre: 'Factura B',
        destinatario: 'Consumidor Final / Monotributo / Exento',
        discriminaIva: false,
    },
    C: {
        letra: 'C',
        codigo: '11',
        nombre: 'Factura C',
        destinatario: 'Consumidor Final (emisor Monotributo)',
        discriminaIva: false,
    },
};

/** Alicuota unica del prototipo: el catalogo de bazar es todo 21%. */
const IVA_RATE = 0.21;

/* ------------------------------------------------------------------ emisor */

/**
 * Datos fiscales del emisor. Son ficticios y viven en un solo lugar para que
 * el dia que exista un alta real se cambien aca y no en el layout.
 */
export const EMISOR = {
    razonSocial: 'ARDEL S.R.L.',
    nombreFantasia: 'Ardel',
    domicilio: 'Av. Rivadavia 4521 - C.A.B.A.',
    cuit: '30712345678',
    ingresosBrutos: '901-123456-7',
    inicioActividades: '01/03/2019',
    condicionIva: 'IVA Responsable Inscripto',
    puntoVenta: 3,
} as const;

/* ----------------------------------------------------------------- helpers */

const money = (n: number) =>
    new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        .format(Number.isFinite(n) ? n : 0);

/** CUIT/DNI con la separacion habitual: 30-71234567-8. */
const formatCuit = (raw: string) => {
    const digits = (raw || '').replace(/\D/g, '');
    if (digits.length !== 11) return raw || '-';
    return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
};

const pad = (value: number | string, length: number) => String(value).padStart(length, '0');

const todayAR = () => {
    const d = new Date();
    return `${pad(d.getDate(), 2)}/${pad(d.getMonth() + 1, 2)}/${d.getFullYear()}`;
};

const addDays = (days: number) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return `${pad(d.getDate(), 2)}/${pad(d.getMonth() + 1, 2)}/${d.getFullYear()}`;
};

/** 'DD/MM/YYYY' -> 'YYYYMMDD', que es como lo espera el codigo de barras. */
const toCompactDate = (ar: string) => {
    const [d, m, y] = ar.split('/');
    return `${y}${m}${d}`;
};

/* ---------------------------------------------------- comprobante simulado */

export interface Comprobante {
    tipo: TipoFactura;
    puntoVenta: number;
    numero: number;
    /** '0003-00000127' */
    etiqueta: string;
    fecha: string;
    cae: string;
    vtoCae: string;
    /** Venta de origen. */
    orderId: string;
    receptor: Receptor;
    totales: Totales;
}

export interface Receptor {
    razonSocial: string;
    /** CUIT (11 digitos) o DNI (7-8). Vacio = consumidor final sin identificar. */
    documento: string;
    tipoDocumento: 'CUIT' | 'DNI' | 'Sin identificar';
    domicilio: string;
    condicionIva: string;
}

export interface Totales {
    /** Neto gravado (sin IVA) para A; importe total para B y C. */
    neto: number;
    iva: number;
    total: number;
}

/**
 * Digito verificador del CAE simulado: mismo modulo 10 que usa AFIP para el
 * codigo de barras. Se reutiliza aca para que el CAE "parezca" uno real.
 */
const modulo10 = (digits: string): number => {
    let odd = 0;
    let even = 0;
    // Se numera de derecha a izquierda: la ultima posicion es impar.
    for (let i = 0; i < digits.length; i++) {
        const digit = Number(digits[digits.length - 1 - i]);
        if (i % 2 === 0) odd += digit;
        else even += digit;
    }
    const total = odd * 3 + even;
    return (10 - (total % 10)) % 10;
};

/** CAE de 14 digitos, deterministico a partir de la venta y el tipo. */
const fakeCae = (tipo: TipoFactura, numero: number, orderId: string): string => {
    const seedSource = `${tipo}${numero}${orderId}`;
    let seed = 0;
    for (let i = 0; i < seedSource.length; i++) {
        seed = (seed * 31 + seedSource.charCodeAt(i)) >>> 0;
    }
    // Los CAE reales arrancan con el anio en dos digitos.
    const year = String(new Date().getFullYear()).slice(2);
    const body = pad(seed % 100000000000, 11).slice(0, 11);
    const base = `${year}${body}`.slice(0, 13);
    return `${base}${modulo10(base)}`;
};

/**
 * Arma el comprobante a partir de una venta ya registrada.
 * `numero` lo provee quien llama (el contador de la pantalla de facturacion).
 */
export const buildComprobante = (
    order: Order,
    tipo: TipoFactura,
    numero: number,
    receptor: Receptor,
): Comprobante => {
    const bruto = Number(order.Total) || 0;
    const def = TIPOS_FACTURA[tipo];

    // En A el precio de lista se considera final: el neto se obtiene sacandole
    // el IVA, de modo que el total impreso coincida con lo que se cobro.
    const totales: Totales = def.discriminaIva
        ? {
              neto: bruto / (1 + IVA_RATE),
              iva: bruto - bruto / (1 + IVA_RATE),
              total: bruto,
          }
        : { neto: bruto, iva: 0, total: bruto };

    return {
        tipo,
        puntoVenta: EMISOR.puntoVenta,
        numero,
        etiqueta: `${pad(EMISOR.puntoVenta, 4)}-${pad(numero, 8)}`,
        fecha: todayAR(),
        cae: fakeCae(tipo, numero, order.ID),
        vtoCae: addDays(10),
        orderId: order.ID,
        receptor,
        totales,
    };
};

/* --------------------------------------------- codigo de barras Interleaved 2 of 5 */

const I25_PATTERNS: Record<string, string> = {
    // 'n' = barra/espacio angosto, 'w' = ancho.
    '0': 'nnwwn', '1': 'wnnnw', '2': 'nwnnw', '3': 'wwnnn', '4': 'nnwnw',
    '5': 'wnwnn', '6': 'nwwnn', '7': 'nnnww', '8': 'wnnwn', '9': 'nwnwn',
};

/**
 * Dibuja el codigo de barras del comprobante (Interleaved 2 of 5, como el que
 * imprime AFIP al pie). Las cifras se codifican de a pares: la primera en las
 * barras y la segunda en los espacios.
 */
const drawI25 = (
    doc: jsPDF,
    digits: string,
    x: number,
    y: number,
    width: number,
    height: number,
) => {
    const data = digits.length % 2 === 0 ? digits : `0${digits}`;

    // Ancho total en modulos: guardas (4 + 3) mas 7 modulos por par de digitos.
    const modules = 7 + (data.length / 2) * 14;
    const unit = width / modules;

    let cursor = x;
    doc.setFillColor(0, 0, 0);

    const bar = (widthUnits: number, dark: boolean) => {
        if (dark) doc.rect(cursor, y, unit * widthUnits, height, 'F');
        cursor += unit * widthUnits;
    };

    // Guarda inicial: nnnn
    bar(1, true); bar(1, false); bar(1, true); bar(1, false);

    for (let i = 0; i < data.length; i += 2) {
        const barsPattern = I25_PATTERNS[data[i]];
        const spacesPattern = I25_PATTERNS[data[i + 1]];
        for (let k = 0; k < 5; k++) {
            bar(barsPattern[k] === 'w' ? 3 : 1, true);
            bar(spacesPattern[k] === 'w' ? 3 : 1, false);
        }
    }

    // Guarda final: wnn
    bar(3, true); bar(1, false); bar(1, true);
};

/**
 * Cadena del codigo de barras segun RG 4291:
 * CUIT + tipo de comprobante + punto de venta + CAE + vencimiento + DV.
 */
const barcodePayload = (c: Comprobante): string => {
    const base =
        EMISOR.cuit +
        pad(TIPOS_FACTURA[c.tipo].codigo, 2) +
        pad(c.puntoVenta, 4) +
        c.cae +
        toCompactDate(c.vtoCae);
    return `${base}${modulo10(base)}`;
};

/* ------------------------------------------------------------------ QR AFIP */

/** URL del QR de ARCA: https://www.afip.gob.ar/fe/qr/?p=<json en base64>. */
const qrPayload = (c: Comprobante): string => {
    const [d, m, y] = c.fecha.split('/');
    const data = {
        ver: 1,
        fecha: `${y}-${m}-${d}`,
        cuit: Number(EMISOR.cuit),
        ptoVta: c.puntoVenta,
        tipoCmp: Number(TIPOS_FACTURA[c.tipo].codigo),
        nroCmp: c.numero,
        importe: Number(c.totales.total.toFixed(2)),
        moneda: 'PES',
        ctz: 1,
        tipoDocRec: c.receptor.tipoDocumento === 'CUIT' ? 80 : c.receptor.tipoDocumento === 'DNI' ? 96 : 99,
        nroDocRec: Number((c.receptor.documento || '0').replace(/\D/g, '')) || 0,
        tipoCodAut: 'E',
        codAut: Number(c.cae),
    };
    const json = JSON.stringify(data);
    // btoa no acepta caracteres > 255: se pasa por UTF-8 byte a byte primero.
    const bytes = new TextEncoder().encode(json);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return `https://www.afip.gob.ar/fe/qr/?p=${btoa(binary)}`;
};

const drawQr = (doc: jsPDF, text: string, x: number, y: number, size: number) => {
    let matrix: boolean[][];
    try {
        matrix = generateQrMatrix(text);
    } catch {
        return; // Un QR que no entra no debe impedir que salga la factura.
    }

    const quiet = 2; // zona silenciosa exigida por la norma, en modulos
    const modules = matrix.length + quiet * 2;
    const unit = size / modules;

    doc.setFillColor(255, 255, 255);
    doc.rect(x, y, size, size, 'F');
    doc.setFillColor(0, 0, 0);

    for (let r = 0; r < matrix.length; r++) {
        for (let c = 0; c < matrix.length; c++) {
            if (!matrix[r][c]) continue;
            doc.rect(x + (c + quiet) * unit, y + (r + quiet) * unit, unit, unit, 'F');
        }
    }
};

/* ------------------------------------------------------------------ layout */

const GRAY: [number, number, number] = [90, 90, 90];
const LINE: [number, number, number] = [0, 0, 0];

/**
 * Recuadro central con la letra del comprobante y su codigo, montado sobre la
 * linea que divide el encabezado. Es la marca visual mas reconocible del
 * formato de ARCA.
 */
const drawLetterBox = (doc: jsPDF, tipo: TipoFactura, cx: number, top: number) => {
    const w = 16;
    const h = 18;
    const x = cx - w / 2;

    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.4);
    doc.rect(x, top, w, h, 'FD');

    doc.setTextColor(0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(26);
    doc.text(tipo, cx, top + 11, { align: 'center' });

    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'normal');
    doc.text(`COD. ${TIPOS_FACTURA[tipo].codigo}`, cx, top + 15.5, { align: 'center' });
};

/** Una etiqueta gris chica con su valor al lado, en una sola linea. */
const labeled = (doc: jsPDF, label: string, value: string, x: number, y: number) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(0);
    doc.text(label, x, y);
    const offset = doc.getTextWidth(label) + 1.5;
    doc.setFont('helvetica', 'normal');
    doc.text(value, x + offset, y);
};

/**
 * Genera el PDF de la factura y lo abre en una pestana nueva ademas de
 * descargarlo, igual que hace el resto de los comprobantes del panel.
 */
export const generateFacturaPDF = (order: Order, comprobante: Comprobante) => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 10;
    const innerW = pageW - margin * 2;
    const cx = pageW / 2;
    const def = TIPOS_FACTURA[comprobante.tipo];

    /* ------------------------------------------------------ encabezado */

    const headTop = margin;
    const headH = 40;

    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.4);
    doc.rect(margin, headTop, innerW, headH);
    // Linea vertical que parte el encabezado en emisor | comprobante.
    doc.line(cx, headTop, cx, headTop + headH);

    // --- columna izquierda: emisor
    let y = headTop + 9;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.setTextColor(0);
    doc.text(EMISOR.nombreFantasia.toUpperCase(), margin + 6, y);

    y += 7;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('Razón Social:', margin + 6, y);
    doc.setFont('helvetica', 'normal');
    doc.text(EMISOR.razonSocial, margin + 28, y);

    y += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('Domicilio Comercial:', margin + 6, y);
    doc.setFont('helvetica', 'normal');
    doc.text(EMISOR.domicilio, margin + 38, y);

    y += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('Condición frente al IVA:', margin + 6, y);
    doc.setFont('helvetica', 'normal');
    doc.text(EMISOR.condicionIva, margin + 44, y);

    // --- columna derecha: identificacion del comprobante
    let ry = headTop + 9;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text('FACTURA', cx + 8, ry);

    ry += 8;
    doc.setFontSize(9);
    labeled(doc, 'Punto de Venta:', pad(comprobante.puntoVenta, 4), cx + 8, ry);
    labeled(doc, 'Comp. Nro:', pad(comprobante.numero, 8), cx + 48, ry);

    ry += 6;
    doc.setFontSize(8);
    labeled(doc, 'Fecha de Emisión:', comprobante.fecha, cx + 8, ry);

    ry += 5;
    labeled(doc, 'CUIT:', formatCuit(EMISOR.cuit), cx + 8, ry);

    ry += 5;
    labeled(doc, 'Ingresos Brutos:', EMISOR.ingresosBrutos, cx + 8, ry);

    ry += 5;
    labeled(doc, 'Inicio de Actividades:', EMISOR.inicioActividades, cx + 8, ry);

    // El recuadro de letra va montado sobre la division, pisando el borde.
    drawLetterBox(doc, comprobante.tipo, cx, headTop + 2);

    /* -------------------------------------------------- datos del receptor */

    let boxY = headTop + headH + 3;
    const receptorH = 22;
    doc.rect(margin, boxY, innerW, receptorH);

    y = boxY + 6;
    const r = comprobante.receptor;
    const docLabel = r.tipoDocumento === 'Sin identificar' ? 'DNI:' : `${r.tipoDocumento}:`;
    const docValue = r.tipoDocumento === 'CUIT' ? formatCuit(r.documento) : (r.documento || '-');

    doc.setFontSize(8);
    labeled(doc, docLabel, docValue, margin + 6, y);
    labeled(doc, 'Apellido y Nombre / Razón Social:', r.razonSocial, margin + 60, y);

    y += 6;
    labeled(doc, 'Condición frente al IVA:', r.condicionIva, margin + 6, y);

    y += 6;
    labeled(doc, 'Domicilio:', r.domicilio || '-', margin + 6, y);
    labeled(doc, 'Condición de venta:', order.MetodoPago || 'Contado', margin + 110, y);

    /* ---------------------------------------------------- detalle de items */

    // En A la columna de precio va sin IVA para que sume con el neto de abajo.
    const netFactor = def.discriminaIva ? 1 / (1 + IVA_RATE) : 1;

    const body = (order.Items || []).map((item, index) => {
        const cantidad = Number(item.Cantidad) || 0;
        const unitario = (Number(item.Precio) || 0) * netFactor;
        const bonif = (Number(item.Descuento) || 0) * netFactor;
        const subtotal = unitario * cantidad - bonif;

        return [
            pad(index + 1, 3),
            item.Producto || '-',
            String(cantidad),
            'unidad',
            money(unitario),
            bonif > 0 ? money(bonif) : '0,00',
            ...(def.discriminaIva ? ['21%'] : []),
            money(subtotal),
        ];
    });

    const head = [
        [
            'Código',
            'Producto / Servicio',
            'Cantidad',
            'U. medida',
            'Precio Unit.',
            'Bonif.',
            ...(def.discriminaIva ? ['Alícuota IVA'] : []),
            'Subtotal',
        ],
    ];

    autoTable(doc, {
        head,
        body,
        startY: boxY + receptorH + 3,
        margin: { left: margin, right: margin },
        theme: 'grid',
        styles: {
            font: 'helvetica',
            fontSize: 7.5,
            cellPadding: 1.6,
            lineColor: [150, 150, 150],
            lineWidth: 0.1,
            textColor: 20,
        },
        headStyles: {
            fillColor: [235, 235, 235],
            textColor: 0,
            fontStyle: 'bold',
            halign: 'center',
            lineColor: [120, 120, 120],
        },
        columnStyles: {
            0: { halign: 'center', cellWidth: 14 },
            2: { halign: 'right', cellWidth: 16 },
            3: { halign: 'center', cellWidth: 18 },
            4: { halign: 'right', cellWidth: 24 },
            5: { halign: 'right', cellWidth: 18 },
            ...(def.discriminaIva
                ? { 6: { halign: 'center' as const, cellWidth: 20 }, 7: { halign: 'right' as const, cellWidth: 26 } }
                : { 6: { halign: 'right' as const, cellWidth: 26 } }),
        },
    });

    /* ------------------------------------------------------------ totales */

    const afterTable = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
    const totalsW = 78;
    const totalsX = pageW - margin - totalsW;
    const rows: [string, string][] = def.discriminaIva
        ? [
              ['Subtotal:', `$ ${money(comprobante.totales.neto)}`],
              ['Importe Neto Gravado:', `$ ${money(comprobante.totales.neto)}`],
              ['IVA 21%:', `$ ${money(comprobante.totales.iva)}`],
          ]
        : [['Subtotal:', `$ ${money(comprobante.totales.total)}`]];

    let ty = afterTable + 6;
    doc.setFontSize(8.5);
    rows.forEach(([label, value]) => {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0);
        doc.text(label, totalsX, ty);
        doc.text(value, pageW - margin, ty, { align: 'right' });
        ty += 5;
    });

    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.3);
    doc.line(totalsX, ty - 3, pageW - margin, ty - 3);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.text('Importe Total:', totalsX, ty + 2);
    doc.text(`$ ${money(comprobante.totales.total)}`, pageW - margin, ty + 2, { align: 'right' });

    // En B y C la leyenda obligatoria reemplaza a la discriminacion de IVA.
    if (!def.discriminaIva) {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(7);
        doc.setTextColor(...GRAY);
        const leyenda =
            comprobante.tipo === 'C'
                ? 'El crédito fiscal discriminado en el presente comprobante sólo podrá ser computado a efectos del Régimen de Sostenimiento e Inclusión Fiscal para Pequeños Contribuyentes.'
                : 'Régimen de Transparencia Fiscal al Consumidor (Ley 27.743). IVA contenido en el precio.';
        doc.text(doc.splitTextToSize(leyenda, 95), margin, afterTable + 8);
    }

    /* --------------------------------------------------- pie: CAE, barras, QR */

    const footTop = Math.max(ty + 12, afterTable + 30);
    const qrSize = 30;

    drawQr(doc, qrPayload(comprobante), margin, footTop, qrSize);

    // Logo textual de ARCA, como en el comprobante oficial.
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(0);
    doc.text('ARCA', margin + qrSize + 5, footTop + 8);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(...GRAY);
    doc.text('Agencia de Recaudación', margin + qrSize + 5, footTop + 12);
    doc.text('y Control Aduanero', margin + qrSize + 5, footTop + 15.5);

    doc.setFontSize(6.5);
    doc.text('Comprobante Autorizado', margin + qrSize + 5, footTop + 22);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(6);
    doc.text(
        'Esta Administración Federal no se responsabiliza por los datos ingresados en el detalle de la operación',
        margin + qrSize + 5,
        footTop + 25.5,
    );

    // Codigo de barras y CAE alineados a la derecha.
    const barcodeW = 78;
    const barcodeX = pageW - margin - barcodeW;
    const payload = barcodePayload(comprobante);

    drawI25(doc, payload, barcodeX, footTop, barcodeW, 12);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(0);
    doc.text(payload, barcodeX + barcodeW / 2, footTop + 15.5, { align: 'center' });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text(`CAE N°: ${comprobante.cae}`, pageW - margin, footTop + 22, { align: 'right' });
    doc.text(`Fecha de Vto. de CAE: ${comprobante.vtoCae}`, pageW - margin, footTop + 27, { align: 'right' });

    // Referencia a la venta que origina el comprobante: no es parte del formato
    // de ARCA, pero permite rastrear el papel contra el sistema.
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(...GRAY);
    doc.text(`Origen: venta #VTA-${order.ID}`, margin, footTop + qrSize + 4);

    const fileName = `Factura_${comprobante.tipo}_${comprobante.etiqueta}`;
    window.open(doc.output('bloburl'), '_blank');
    doc.save(`${fileName}.pdf`);
};
